import type { CSSProperties } from "react";

import type { DataTableColumn } from "./table-features";
import type { PinnedOffsets } from "./use-pinned-offsets";

export interface StickyCellProps {
  dataAttrs: {
    "data-sticky"?: true;
    "data-sticky-right"?: true;
    "data-sticky-key"?: string;
  };
  style: CSSProperties | undefined;
}

/**
 * Builds the offset style and `data-sticky*` attributes for a pinned column
 * cell. `position: sticky` / `z-index` come from table.module.css via the
 * data attributes; only the per-column offsets are inline. Returns empty
 * values for non-pinned columns so the caller can spread unconditionally.
 *
 * `offsets` holds widths measured from the rendered header (see
 * `usePinnedOffsets`); TanStack's declared sizes understate any column whose
 * content renders wider — as a translated label does — so they only serve as
 * the pre-measurement fallback. `tagColumnId` stamps `data-sticky-key` for that
 * measurement — leaf headers only, since nothing else is measured.
 */
export function getStickyCellProps<T>(
  column: DataTableColumn<T>,
  offsets?: PinnedOffsets,
  { tagColumnId = false }: { tagColumnId?: boolean } = {},
): StickyCellProps {
  const pinned = column.getIsPinned();
  if (!pinned) return { dataAttrs: {}, style: undefined };
  // v9 pins by logical direction; the CSS offsets stay physical (`left`/
  // `right`), since the table itself is not RTL-aware.
  //
  // Offsets are only defined for leaf columns (`getIndex` looks the column up
  // among the leaves), so a group column measures from its outermost leaf on
  // the pinned side. For a leaf column this is the column itself.
  const leaves = column.getLeafColumns();
  // A group counts as pinned as soon as *one* of its columns is (TanStack's
  // rule), but a partly-pinned group header spans scrolling columns too — it
  // would hang over them. Only pin the header when the whole group is pinned.
  if (leaves.length > 1 && leaves.some((l) => l.getIsPinned() !== pinned)) {
    return { dataAttrs: {}, style: undefined };
  }
  const anchor =
    pinned === "start" ? (leaves[0] ?? column) : (leaves.at(-1) ?? column);
  // The measured map is keyed by leaf id, so a group header resolves to the
  // same offset its anchor leaf sits at — which is what spanning it means.
  const offset =
    offsets?.get(anchor.id) ??
    (pinned === "start" ? anchor.getStart("start") : anchor.getAfter("end"));

  return {
    dataAttrs: {
      "data-sticky": pinned === "start" ? true : undefined,
      "data-sticky-right": pinned === "end" ? true : undefined,
      "data-sticky-key": tagColumnId ? column.id : undefined,
    },
    style:
      pinned === "start" ? { left: `${offset}px` } : { right: `${offset}px` },
  };
}
